import type { SuretyAccountId } from "@inrev/common";
import {
	BLOCSizeCard,
	CurrentRatioFYECard,
	DebtEquityCard,
	EquityFYECard,
	NetIncomeFYECard,
	ProfitabilityYTDCard,
	RevenueFYECard,
	useFinancialsMetrics,
	WorkingCapitalFYECard,
} from "@portal/components/financials";
import type {
	BalanceSheet,
	FinancialPeriodDto,
	IncomeStatement,
} from "@portal/components/financials/utils";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { type FC, useState } from "react";
import { FinancialMetricsCompactStrip } from "./FinancialMetricsCompactStrip";

export type FinancialMetricsProps = {
	accountId: SuretyAccountId;
	periods: FinancialPeriodDto[];
	incomeStatements: IncomeStatement[];
	balanceSheets: BalanceSheet[];
};

export const FinancialMetrics: FC<FinancialMetricsProps> = ({
	accountId,
	periods,
	incomeStatements,
	balanceSheets,
}) => {
	const { isAdmin } = useAuthenticatedUser();
	const [isExpanded, setIsExpanded] = useState(false);
	const metrics = useFinancialsMetrics(periods, incomeStatements, balanceSheets);
	const { recentFYE, previousFYE, recentYTD } = metrics;

	return (
		<div data-amp-mask data-sentry-mask>
			<FinancialMetricsCompactStrip
				metrics={metrics}
				isExpanded={isExpanded}
				onToggle={() => setIsExpanded((expanded) => !expanded)}
			>
				<ProfitabilityYTDCard
					accountId={accountId}
					recentYTD={recentYTD}
					isManual={recentYTD?.isManual}
					canEdit={isAdmin}
				/>
				<RevenueFYECard recentFYE={recentFYE} previousFYE={previousFYE} />
				<NetIncomeFYECard
					accountId={accountId}
					recentFYE={recentFYE}
					previousFYE={previousFYE}
					isManual={recentFYE?.isManual}
					canEdit={isAdmin}
				/>
				<WorkingCapitalFYECard recentFYE={recentFYE} previousFYE={previousFYE} />
				<CurrentRatioFYECard recentFYE={recentFYE} />
				<EquityFYECard recentFYE={recentFYE} previousFYE={previousFYE} />
				<DebtEquityCard recentFYE={recentFYE} />
				<BLOCSizeCard accountId={accountId} recentFYE={recentFYE} canEdit={isAdmin} />
			</FinancialMetricsCompactStrip>
		</div>
	);
};
